<template>
  <div>
    <h1 class="title is-size-4-mobile">{{ $t("User management") }}</h1>

    <field-horizontal-table :label="$t('Team')">
      <member-row
        v-for="(member, email) in members"
        :key="email"
        :email="email"
        :role="member.role"
        :roles="roles"
        :failureMsg="memberChangeRoleFailureMsg[email]"
        :successMsg="memberChangeRoleSuccessMsg[email]"
        @changeRole="setRoleToMember"
        @delete="openRemoveMemberConfirmation"
      />
    </field-horizontal-table>

    <field-horizontal :label="$t('Add user')">
      <input-field
        type="email"
        v-model="memberEmail"
        @focus="memberEmail_onFocus"
        :errors="memberEmail_errorsIfTouched"
      />
      <button
        slot="right"
        class="button is-primary"
        :disabled="!memberEmail_valid || !memberEmail_dirty"
        @click="addMemberToProject"
      >
        {{ $t("Add") }}
      </button>
      <field-message
        slot="message"
        :success="$t(addMemberResultSuccessMsg)"
        :failure="$t(addMemberResultFailureMsg)"
      />
    </field-horizontal>

    <modal-confirmation
      :is-open="!!removingMember"
      :title="$t('Are you sure you want to remove this member?')"
      :confirmText="$t('Remove')"
      @confirm="removeMemberFromProject"
      @close="closeRemoveMemberConfirmation"
    >
      {{
        $t(
          "Once removed, {0} will no longer have granted access to manage this location!",
          removingMember
        )
      }}
    </modal-confirmation>
  </div>
</template>

<script>
import { state, actions } from "@/store"
import { resetTimeout } from "@/utils"
import { validator, required, email } from "@/validator"
import {
  InputField,
  FieldHorizontal,
  FieldMessage,
  FieldHorizontalTable
} from "@/components/form"
import MemberRow from "@/components/settings/member-row"
import { ModalConfirmation } from "@/components/modal"

const MSG_TIMEOUT_MS = 3000

export default {
  name: "user-management",
  mixins: [validator],
  components: {
    InputField,
    FieldHorizontal,
    FieldHorizontalTable,
    FieldMessage,
    MemberRow,
    ModalConfirmation
  },
  data() {
    return {
      memberEmail: "",
      roles: ["admin", "member"],
      addMemberResultSuccessMsg: undefined,
      addMemberResultFailureMsg: undefined,
      memberChangeRoleSuccessMsg: {},
      memberChangeRoleFailureMsg: {},
      removingMember: "",
      pairingCode: ""
    }
  },
  computed: {
    members() {
      return state.project.members || {}
    },
    loggedUserEmail() {
      return state.user.email
    }
  },
  validations: {
    memberEmail: [required, email]
  },
  methods: {
    addMemberToProject() {
      this.addMemberResultFailureMsg = undefined
      this.addMemberResultSuccessMsg = undefined
      actions
        .addMemberToProject(this.memberEmail, "member")
        .then(() => {
          this.memberEmail = ""
          this.memberEmail_touched = false
          this.addMemberResultSuccessMsg = "Add member success"
          resetTimeout(
            "addMemberToProject",
            () => {
              this.addMemberResultSuccessMsg = undefined
            },
            MSG_TIMEOUT_MS
          )
        })
        .catch(e => {
          switch (e && e.name) {
            case "member_already_exists": {
              this.addMemberResultFailureMsg = "member-already-exists"
              break
            }
            default:
              this.addMemberResultFailureMsg = "add-member-error"
          }
          this.$logger.error(this.addMemberResultFailureMsg, e)
        })
    },
    setRoleToMember(email, role) {
      this.$set(this.memberChangeRoleFailureMsg, email, undefined)
      this.$set(this.memberChangeRoleSuccessMsg, email, undefined)
      actions
        .setRoleToMember(email, role)
        .then(() => {
          this.memberChangeRoleSuccessMsg[email] = "Saved"
          resetTimeout(
            "setRoleToMember_" + email,
            () => {
              this.memberChangeRoleSuccessMsg[email] = undefined
            },
            MSG_TIMEOUT_MS
          )
        })
        .catch(e => {
          this.memberChangeRoleFailureMsg[email] =
            "Failed to change role to team member."
          this.$logger.error("change-role-error", e)
        })
    },
    removeMemberFromProject() {
      const member = this.removingMember
      this.closeRemoveMemberConfirmation()
      actions
        .removeMemberFromProject(member)
        .catch(actions.addError("remove-member-error"))
    },
    openRemoveMemberConfirmation(email) {
      this.removingMember = email
    },
    closeRemoveMemberConfirmation() {
      this.removingMember = ""
    }
  }
}
</script>
